import useTrackComponent from 'hooks/eventTracking/useTrackComponent'
import { ArrowLeftSmIcon, ArrowRightSmIcon, IconButton, styled, Typography, useTheme } from 'opensolar-ui'
import React from 'react'
import { useTranslate } from 'react-admin'
import { NavLink } from 'react-router-dom'
import { ActionType } from 'types/tracking'
import { ProductLoadingTile } from '../catalogv3/ProductLoadingTile'
import ProductTile from '../catalogv3/ProductTile'
import { majorComponentCategories } from '../configs'

interface CarouselProductGridProps {
  componentsData: any[]
  carouselMode: boolean
  title?: string
  description?: string
  viewAllLink?: string
  category?: string
  /** When true, show skeleton tiles only (SWR: not when refetching with cached rows). */
  loading?: boolean
  /** Raw in-flight fetch; drives carousel header visibility and complements arrow enablement. */
  isFetching?: boolean
  pageSize?: number // Just used for the loading skeleton
  /** Total items available from the API (when the fetch is paginated). Used for View All on config-driven carousels. */
  totalCount?: number
}

const CarouselContainer = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  position: 'relative',
  padding: '16px 0',
}))

const ScrollContainer = styled('div')<{ carouselMode: boolean }>(({ carouselMode, theme }) => ({
  display: 'flex',
  gap: theme.spacing(3),
  overflowX: carouselMode ? 'auto' : 'visible',
  overflowY: 'hidden',
  flexWrap: carouselMode ? 'nowrap' : 'wrap',
  scrollbarWidth: 'none',
}))

const TitleContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 24,
})

const ScrollButtonGroup = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  gap: 8,
})

const ScrollIconButton = styled(IconButton)({
  display: 'flex',
  width: 40,
  height: 40,
  justifyContent: 'center',
  alignItems: 'center',
  gap: 8,
  borderRadius: 8,
  border: '1px solid #E6E6E6',
  background: '#FFF',
  '& svg path': {
    strokeWidth: 1.5,
    stroke: '#6B6A76',
  },
})

const StyledNavLink = styled(NavLink)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  '&:visited, &:link': {
    color: '#1751D0',
  },
}))

const ProductGrid = ({
  title,
  description,
  componentsData,
  carouselMode,
  viewAllLink,
  category,
  loading,
  isFetching = false,
  pageSize = 10,
  totalCount,
}: CarouselProductGridProps) => {
  const scrollContainerRef = React.useRef<HTMLDivElement>(null)
  const translate = useTranslate()
  const theme = useTheme()
  const { trackUserInteraction } = useTrackComponent({
    componentKey: 'product_grid',
    eventName: 'Hardware Product',
  })

  // conditions to show view all link – two variations – config driven and general components
  const isConfigDriven = !majorComponentCategories.includes(category ?? '')
  const itemCount = totalCount ?? componentsData?.length ?? 0
  const showViewAll =
    (isConfigDriven && viewAllLink && itemCount > pageSize) || (!isConfigDriven && viewAllLink)

  const scroll = (direction: 'left' | 'right') => {
    const container = scrollContainerRef.current
    if (container) {
      const scrollAmount = container.clientWidth
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      })
    }
  }

  const onClickViewAll = () => {
    const linkBreakdown = viewAllLink?.split('/')
    trackUserInteraction(
      { type: ActionType.CLICK },
      {
        source: `${category}_view_all_button`,
        ...(linkBreakdown?.includes('distributor') ? { distributor: linkBreakdown[5] } : {}),
      }
    )
  }

  if (!loading && (!componentsData || componentsData.length === 0)) {
    return null
  }

  return (
    <CarouselContainer>
      {(loading || isFetching || (componentsData?.length ?? 0) > 0) && (
        <>
          <TitleContainer>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {title && <Typography textVariant="h5Bold">{title}</Typography>}
                {showViewAll && (
                  <Typography variant="body1" colorHex="#1751D0">
                    <StyledNavLink
                      to={{ pathname: viewAllLink, state: { componentsData, title } }}
                      onClick={onClickViewAll}
                    >
                      {translate('View All')}
                    </StyledNavLink>
                  </Typography>
                )}
              </div>
              {description && (
                <Typography textVariant="h6" style={{ margin: '4px 0 0' }}>
                  {description}
                </Typography>
              )}
            </div>
            {carouselMode && (
              <ScrollButtonGroup>
                <ScrollIconButton
                  onClick={() => scroll('left')}
                  disabled={loading || (componentsData?.length ?? 0) === 0}
                >
                  <ArrowLeftSmIcon />
                </ScrollIconButton>
                <ScrollIconButton
                  onClick={() => scroll('right')}
                  disabled={loading || (componentsData?.length ?? 0) === 0}
                >
                  <ArrowRightSmIcon />
                </ScrollIconButton>
              </ScrollButtonGroup>
            )}
          </TitleContainer>
        </>
      )}
      <ScrollContainer ref={scrollContainerRef} carouselMode={carouselMode}>
        {loading
          ? Array.from({ length: pageSize }).map((_, index) => <ProductLoadingTile key={index} />)
          : componentsData
            ?.slice(0, carouselMode ? pageSize : componentsData.length)
            .map((data) => <ProductTile component={data} key={data.id} />)}
      </ScrollContainer>
    </CarouselContainer>
  )
}

export default ProductGrid
